import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  DatePicker,
  Input,
  InputNumber,
  Radio,
  Select,
  Table,
  Tag,
  message,
} from "antd";
import moment from "moment";
import { Body } from "../../../components/main-body/body";
import ApiAdminBackfill from "../../../api/admin-backfill";
import ApiSubjectsManagement from "../../../api/subjects-manager";
import "./subject-planning-tools.css";

// Ad-hoc per-subject planning control panel — for one-off interventions when
// a single subject's schedule needs manual nudging (missing tasks, stuck
// weekIndex, debugging a failed planning run, etc.). Distinct from
// planning-audit, which is batch recovery for system-wide cron misses; here
// every action is atomic and explicit — planning never advances weekIndex
// implicitly, weekIndex changes never trigger planning. Two side effects,
// two buttons.

const RANGE_OPTIONS = [
  { value: "today", label: "From today" },
  { value: "weekStart", label: "From start of week" },
  { value: "specific", label: "Specific week" },
];

const STATUS_COLORS = {
  completed: "green",
  failed: "red",
  started: "gold",
};

const formatDateTime = (value) =>
  value ? moment(value).utc().format("DD MMM YYYY, HH:mm") + " UTC" : "—";

const SubjectPlanningTools = () => {
  const [subjects, setSubjects] = useState([]);
  const [subjectId, setSubjectId] = useState();
  const [subjectsLoading, setSubjectsLoading] = useState(false);

  // Plan-section form state
  const [range, setRange] = useState("today");
  const [weekDate, setWeekDate] = useState(moment().startOf("week"));
  const [allowPastDates, setAllowPastDates] = useState(true);
  const [planComment, setPlanComment] = useState("");
  const [planSubmitting, setPlanSubmitting] = useState(false);
  const [planResult, setPlanResult] = useState(null);

  // Week-index form state
  const [newWeekIndex, setNewWeekIndex] = useState(null);
  const [weekComment, setWeekComment] = useState("");
  const [weekSubmitting, setWeekSubmitting] = useState(false);
  const [weekResult, setWeekResult] = useState(null);

  // Runs panel state
  const [runs, setRuns] = useState([]);
  const [runsLoading, setRunsLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setSubjectsLoading(true);
    ApiSubjectsManagement.getSubjectsSimple()
      .then(({ data }) => {
        if (!cancelled) setSubjects(data?.subjects || []);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setSubjectsLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  // Subject lookup is heavier than dropdown labels — we don't have a
  // dedicated /subjects/:id endpoint that returns the live weekIndex /
  // timeZone, so reading those from the simple list is sufficient and
  // avoids a second call. They refresh whenever subject changes.
  const selectedSubject = useMemo(
    () => subjects.find((s) => s.id === subjectId) || null,
    [subjects, subjectId]
  );

  const subjectOptions = useMemo(
    () => subjects.map((s) => ({ value: s.id, label: s.displayId || s.id })),
    [subjects]
  );

  const loadRuns = useCallback(async (id) => {
    if (!id) {
      setRuns([]);
      return;
    }
    setRunsLoading(true);
    try {
      const { data } = await ApiAdminBackfill.getSubjectPlanningRuns(id, { limit: 20 });
      setRuns(data?.runs || []);
    } catch (_e) {
      setRuns([]);
    } finally {
      setRunsLoading(false);
    }
  }, []);

  // Reset section-local state when admin switches subject — stale results
  // from another subject would mislead.
  useEffect(() => {
    setPlanResult(null);
    setWeekResult(null);
    setNewWeekIndex(selectedSubject?.weekIndex ?? null);
    loadRuns(subjectId);
  }, [subjectId, selectedSubject, loadRuns]);

  const handlePlan = async () => {
    if (!subjectId) {
      message.warning("Pick a subject first");
      return;
    }

    const payload = {
      fromWeekStart: range === "weekStart" || range === "specific",
      allowPastDates,
      comment: planComment || undefined,
    };
    if (range === "specific" && weekDate) {
      payload.weekStartDate = weekDate.format("YYYY-MM-DD");
    }

    setPlanSubmitting(true);
    setPlanResult(null);
    try {
      const { data } = await ApiAdminBackfill.planSubject(subjectId, payload);
      setPlanResult({ kind: "success", run: data?.run || null });
      message.success("Planning completed");
      await loadRuns(subjectId);
    } catch (err) {
      const errorMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        "Failed to plan tasks";
      setPlanResult({ kind: "error", message: errorMessage });
      message.error(errorMessage);
    } finally {
      setPlanSubmitting(false);
    }
  };

  const handleWeekIndexUpdate = async (mode) => {
    if (!subjectId) {
      message.warning("Pick a subject first");
      return;
    }

    const payload = { comment: weekComment || undefined };
    if (mode === "absolute") {
      if (!Number.isInteger(newWeekIndex) || newWeekIndex < 0) {
        message.warning("Week index must be a non-negative integer");
        return;
      }
      payload.weekIndex = newWeekIndex;
    } else {
      payload.delta = mode === "inc" ? 1 : -1;
    }

    setWeekSubmitting(true);
    setWeekResult(null);
    try {
      const { data } = await ApiAdminBackfill.updateSubjectWeekIndex(
        subjectId,
        payload
      );
      setWeekResult({ kind: "success", data });

      // Reflect change locally so the read-only display updates without a
      // full subjects refetch.
      setSubjects((prev) =>
        prev.map((s) =>
          s.id === subjectId ? { ...s, weekIndex: data.newWeekIndex } : s
        )
      );
      if (Number.isInteger(data.newWeekIndex)) {
        setNewWeekIndex(data.newWeekIndex);
      }

      message.success(
        data.changed
          ? `weekIndex: ${data.previousWeekIndex} → ${data.newWeekIndex}`
          : "weekIndex unchanged"
      );
    } catch (err) {
      const errorMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        "Failed to update weekIndex";
      setWeekResult({ kind: "error", message: errorMessage });
      message.error(errorMessage);
    } finally {
      setWeekSubmitting(false);
    }
  };

  const runColumns = [
    {
      title: "Status",
      dataIndex: "status",
      width: 110,
      render: (status) => (
        <Tag color={STATUS_COLORS[status] || "default"}>{status}</Tag>
      ),
    },
    {
      title: "Week start",
      dataIndex: "weekStartDate",
      width: 130,
    },
    {
      title: "Trigger",
      dataIndex: "trigger",
      ellipsis: true,
    },
    {
      title: "Tasks created",
      dataIndex: "tasksCreated",
      width: 130,
      render: (value) => value ?? 0,
    },
    {
      title: "Started",
      dataIndex: "startedAt",
      width: 200,
      render: formatDateTime,
    },
    {
      title: "Completed",
      dataIndex: "completedAt",
      width: 200,
      render: formatDateTime,
    },
    {
      title: "Error",
      dataIndex: "error",
      ellipsis: true,
      render: (value) => value || "—",
    },
  ];

  return (
    <Body header={{ title: "Subject planning tools" }} scroll>
      <div className="spt-page">
        <div className="spt-subject-panel">
          <div className="spt-subject-row">
            <Select
              showSearch
              allowClear
              optionFilterProp="label"
              placeholder="Pick a subject by displayId…"
              loading={subjectsLoading}
              value={subjectId}
              onChange={setSubjectId}
              options={subjectOptions}
              style={{ minWidth: 320 }}
            />
            {selectedSubject && (
              <div className="spt-subject-meta">
                <span>
                  Status: <b>{selectedSubject.status || "—"}</b>
                </span>
                <span>
                  weekIndex: <b>{selectedSubject.weekIndex ?? "—"}</b>
                </span>
                <span>
                  TZ: <b>{selectedSubject.timeZone || "UTC"}</b>
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="spt-section">
          <div className="spt-section__title">1. Plan tasks</div>
          <div className="spt-section__desc">
            Triggers <code>planSubjectTasksForWeek</code> for this subject.
            Does NOT change the program weekIndex. Use the second section for
            that.
          </div>

          <div className="spt-form-row">
            <label>Range:</label>
            <Radio.Group
              value={range}
              onChange={(e) => setRange(e.target.value)}
              options={RANGE_OPTIONS}
            />
          </div>

          {range === "specific" && (
            <div className="spt-form-row">
              <label>Week starting:</label>
              <DatePicker
                value={weekDate}
                onChange={setWeekDate}
                allowClear={false}
                format="YYYY-MM-DD"
              />
            </div>
          )}

          <div className="spt-form-row">
            <Checkbox
              checked={allowPastDates}
              onChange={(e) => setAllowPastDates(e.target.checked)}
            >
              Allow past dates
            </Checkbox>
          </div>

          <div className="spt-form-row">
            <label>Comment:</label>
            <Input
              value={planComment}
              onChange={(e) => setPlanComment(e.target.value)}
              placeholder="Optional — appears in audit trail"
              style={{ maxWidth: 360 }}
            />
          </div>

          <Button
            type="primary"
            loading={planSubmitting}
            disabled={!subjectId}
            onClick={handlePlan}
          >
            Plan tasks
          </Button>

          {planResult?.kind === "success" && planResult.run && (
            <div className="spt-result spt-result--success">
              ✓ Ran with status <b>{planResult.run.status}</b>. Tasks created:{" "}
              <b>{planResult.run.tasksCreated || 0}</b>, scenarios:{" "}
              <b>{planResult.run.scenariosCreated || 0}</b>. Week:{" "}
              {planResult.run.weekStartDate}.
            </div>
          )}
          {planResult?.kind === "error" && (
            <div className="spt-result spt-result--error">
              ✗ {planResult.message}
            </div>
          )}
        </div>

        <div className="spt-section">
          <div className="spt-section__title">2. Week index</div>
          <div className="spt-section__desc">
            Only updates <code>subject.weekIndex</code>. Does NOT plan or
            reschedule anything — run section 1 afterwards if you need new
            tasks generated for the new week.
          </div>

          <div className="spt-form-row">
            <label>Current:</label>
            <b>{selectedSubject?.weekIndex ?? "—"}</b>
            <Button
              size="small"
              disabled={!subjectId || weekSubmitting}
              onClick={() => handleWeekIndexUpdate("dec")}
            >
              −1
            </Button>
            <Button
              size="small"
              disabled={!subjectId || weekSubmitting}
              onClick={() => handleWeekIndexUpdate("inc")}
            >
              +1
            </Button>
          </div>

          <div className="spt-form-row">
            <label>Set to:</label>
            <InputNumber
              min={0}
              value={newWeekIndex}
              onChange={setNewWeekIndex}
              style={{ width: 120 }}
            />
            <Button
              disabled={!subjectId || weekSubmitting}
              onClick={() => handleWeekIndexUpdate("absolute")}
            >
              Update
            </Button>
          </div>

          <div className="spt-form-row">
            <label>Comment:</label>
            <Input
              value={weekComment}
              onChange={(e) => setWeekComment(e.target.value)}
              placeholder="Optional — appears in audit trail"
              style={{ maxWidth: 360 }}
            />
          </div>

          {weekResult?.kind === "success" && (
            <div className="spt-result spt-result--success">
              ✓ {weekResult.data.changed
                ? `weekIndex: ${weekResult.data.previousWeekIndex} → ${weekResult.data.newWeekIndex}`
                : "weekIndex unchanged"}
            </div>
          )}
          {weekResult?.kind === "error" && (
            <div className="spt-result spt-result--error">
              ✗ {weekResult.message}
            </div>
          )}
        </div>

        <div className="spt-section">
          <div className="spt-section__title">3. Recent planning runs</div>
          <div className="spt-section__desc">
            Last 20 entries from the planning ledger for this subject — useful
            to see what's been happening (cron, manual, recovery, etc.).
          </div>
          <Table
            rowKey="id"
            size="small"
            loading={runsLoading}
            dataSource={runs}
            columns={runColumns}
            pagination={false}
            locale={{ emptyText: subjectId ? "No planning runs recorded" : "Pick a subject to see history" }}
            scroll={{ x: 1100 }}
          />
        </div>
      </div>
    </Body>
  );
};

export default SubjectPlanningTools;
